import { getDomain } from './url';

/**
 * Legacy "domain probe" cookies are named `_gd<timestamp>` (name === value).
 *
 * They are written by a "find the broadest writable domain" Custom HTML tag in
 * the GTM container (GTM-5C6K8FZ). The tag plants a uniquely-named probe cookie
 * on the parent domain (e.g. `.gomotive.com`) and never removes it, so one
 * cookie leaks on every page load. Because they are parent-scoped they are sent
 * to every subdomain — including the anonymous live-share (`tracking.`) and
 * dispatch tracking (`trace.`) hosts, which are served from S3 and reject any
 * request whose header section exceeds 8 KB (`RequestHeaderSectionTooLarge`).
 *
 * The tag sets no `path`, so each cookie lands at the default path of the page
 * that wrote it (e.g. `/en-US` for the locale build). A deletion only takes
 * effect when its `path` matches the stored one exactly, so it has to be
 * attempted at every path prefix of the current page. `document.cookie` only
 * exposes cookies whose path matches the current page — and those are exactly
 * the cookies the browser attaches to this page's requests — so the visible
 * set is the set that needs clearing.
 *
 * See AG-9932 (prior history: TSSD-32472, AT-15267).
 */
const PROBE_COOKIE_PATTERN: RegExp = /^_gd\d+$/;
const EXPIRED_DATE: string = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Extracts the names of accumulated domain-probe cookies from a cookie string.
 */
export function getProbeCookieNames(cookieString: string): string[] {
  if (!cookieString) return [];

  return cookieString
    .split(';')
    .map((cookie: string) => cookie.split('=')[0].trim())
    .filter((name: string) => PROBE_COOKIE_PATTERN.test(name));
}

/**
 * Returns every cookie `path` a cookie visible from `pathname` could have been
 * stored under: `/` plus each cumulative segment prefix without a trailing slash.
 *
 * @example getCookiePathCandidates('/en-US/index.html') // ['/', '/en-US', '/en-US/index.html']
 */
export function getCookiePathCandidates(pathname: string): string[] {
  const candidates: string[] = ['/'];
  const segments: string[] = pathname.split('/').filter((segment: string) => segment.length > 0);

  let prefix: string = '';
  segments.forEach((segment: string) => {
    prefix = `${prefix}/${segment}`;
    candidates.push(prefix);
  });

  return candidates;
}

/**
 * Returns every cookie `domain` a parent-scoped cookie could have been stored
 * under: each proper suffix of `hostname` with at least two labels (the bare
 * TLD is never emitted). The GTM tag writes to the broadest domain the browser
 * accepts, so on a regional host the leaked cookie lives on the two-label root,
 * not on the regional domain — which is why every suffix is tried.
 *
 * The configured server domain is listed first purely as a redundant safety
 * net: in every known deployment it is also a suffix of the hostname and is
 * deduped. Deleting a (name, domain, path) combination that does not exist
 * stores nothing, so over-trying is harmless.
 *
 * @example getCookieDomainCandidates('tracking.eu.gomotive.com', 'eu.gomotive.com')
 * // ['.eu.gomotive.com', '.gomotive.com']
 */
export function getCookieDomainCandidates(hostname: string, serverDomain: string): string[] {
  const candidates: string[] = serverDomain ? [`.${serverDomain}`] : [];
  const labels: string[] = hostname.split('.').filter((label: string) => label.length > 0);

  for (let start: number = 1; start <= labels.length - 2; start++) {
    candidates.push(`.${labels.slice(start).join('.')}`);
  }

  return Array.from(new Set(candidates));
}

function expireCookieAtPath(name: string, path: string, domains: string[]): void {
  domains.forEach((domain: string) => {
    document.cookie = `${name}=;expires=${EXPIRED_DATE};path=${path};domain=${domain};`;
  });
  // Host-only variant, in case the writer fell back to the bare hostname.
  document.cookie = `${name}=;expires=${EXPIRED_DATE};path=${path};`;
}

/**
 * Removes accumulated `_gd<timestamp>` probe cookies so they are no longer sent
 * with requests to this host or its subdomains. Safe to call on every boot:
 * these cookies are throwaway and are never read.
 */
export function clearDomainProbeCookies(
  pathname: string = window.location.pathname,
  hostname: string = window.location.hostname,
): void {
  const paths: string[] = getCookiePathCandidates(pathname);
  const domains: string[] = getCookieDomainCandidates(hostname, getDomain());

  // `document.cookie` lists a name once per stored path, so dedupe before fanning out.
  const names: string[] = Array.from(new Set(getProbeCookieNames(document.cookie)));

  names.forEach((name: string) => {
    paths.forEach((path: string) => expireCookieAtPath(name, path, domains));
  });
}
